/* eslint-disable functional/no-loop-statement */
import { CenterContainer } from '@/components/blocks/CenterContainer';
import { DpProductCard } from '@/components/blocks/DpLibrary/DpProductCard';
import { productCardTopButtonsSX } from '@/components/blocks/DpLibrary/DpProductCard/styles/container';
import { newMostTitleSX } from '@/components/blocks/NewMost/styles/typography';
import { ScrollContainer } from '@/components/blocks/ScrollContainer';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import { Grid, IconButton, Typography, styled, useMediaQuery, useTheme } from '@mui/material';
import { FC, useEffect, useRef, useState } from 'react';

type Props = {
	title: string;
	products: {
		title: string;
		category: string;
		image: string;
		price: string;
		colors: string[];
	}[];
	buttons?: string[];
};

export const NewMost: FC<Props> = ({ title: title, products: products, buttons: buttons }) => {
	const [selected, setSelected] = useState<number>(0);
	const scrollContainerRef = useRef<HTMLDivElement>(null);
	const [isEndReached, setIsEndReached] = useState<boolean>(false);
	const [positions, setPositions] = useState<number[]>([]);
	const [positionActive, setPositionActive] = useState<number>(310);

	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

	useEffect(() => {
		if (positions.length === 0) {
			const positionsTemp = [];
			for (let i = 0; i < products.length; i++) {
				positionsTemp.push(i * 310);
			}
			setPositions(positionsTemp);
		}
		const handleScroll = () => {
			if (scrollContainerRef.current) {
				const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
				const isEnd = scrollLeft + clientWidth >= scrollWidth;
				const responses = positions.filter((e) => e > scrollLeft);
				setPositionActive(responses[0]);
				setIsEndReached(isEnd);
			}
		};

		if (scrollContainerRef.current) {
			scrollContainerRef.current.addEventListener('scroll', handleScroll);
		}
	}, [positions, products.length]);

	const handleScrollRight = () => {
		if (scrollContainerRef.current) {
			scrollContainerRef.current.scrollTo({
				left: positionActive,
				behavior: 'smooth',
			});
		}
	};

	const handleScrollLeft = () => {
		if (scrollContainerRef.current) {
			scrollContainerRef.current.scrollTo({
				left: positionActive - (isEndReached ? 310 : 620),
				behavior: 'smooth',
			});
		}
	};
	const handleSelected = (index: number) => setSelected(index);

	return (
		<CenterContainer sx={{ mt: { xs: '30px', sm: '40px' }, position: 'relative' }}>
			<Grid sx={productCardTopButtonsSX}>
				<Typography sx={{ ...newMostTitleSX, mr: '20px', mb: { xs: '16px', sm: '0' } }}>
					{' '}
					{title}{' '}
				</Typography>
				<Grid display={'inline-flex'}>
					{buttons?.map((button: string, index: number) => {
						const active = selected === index;
						return (
							<CustomButton
								isselected={`${active}`}
								onClick={() => handleSelected(index)}
								key={`id_top_button_${index}`}
							>
								{button}
							</CustomButton>
						);
					})}
				</Grid>
			</Grid>
			<ScrollContainer
				ref={scrollContainerRef}
				sx={{
					pb: {
						xs: '33.5px',
						sm: '38px',
					},
				}}
			>
				<Grid container wrap="nowrap" alignItems={'center'}>
					{products.map(
						(
							value: {
								title: string;
								category: string;
								image: string;
								price: string;
								colors: string[];
							},
							index: number
						) => (
							<Grid item key={`id_new_most_${index}`} sx={{ mr: { xs: '8px', sm: '20px' } }}>
								<DpProductCard variants={[]} partNumber={''} {...value} />
							</Grid>
						)
					)}
					{!isMobile && positionActive - (isEndReached ? 0 : 310) > 0 ? (
						<IconButton
							onClick={handleScrollLeft}
							sx={{
								height: '38px',
								width: '38px',
								position: 'absolute',
								left: '10px',
								backgroundColor: '#F1F1F1',
								':hover': { backgroundColor: '#F1F1F1' },
							}}
						>
							<ChevronLeft sx={{ color: 'text.primary' }} />
						</IconButton>
					) : null}
					{!isMobile && !isEndReached ? (
						<IconButton
							onClick={handleScrollRight}
							sx={{
								height: '38px',
								width: '38px',
								position: 'absolute',
								right: '10px',
								backgroundColor: '#F1F1F1',
								':hover': { backgroundColor: '#F1F1F1' },
							}}
						>
							<ChevronRight sx={{ color: 'text.primary' }} />
						</IconButton>
					) : null}
				</Grid>
			</ScrollContainer>
		</CenterContainer>
	);
};

interface CustomButtonProps {
	isselected: any;
}

const CustomButton = styled(Grid)<CustomButtonProps>(({ isselected }) => ({
	padding: '10px 0px',
	textAlign: 'center',
	backgroundColor: isselected === 'true' ? '#333333' : '#F7F7F8',
	border: '1px solid #CBCACC',
	marginRight: '10px',
	borderRadius: '21px',
	cursor: 'pointer',
	fontSize: 'var(--font-size-body)',
	fontWeight: isselected === 'true' ? 'bold' : 'normal',
	color: isselected === 'true' ? '#FFFFFF' : '#333333',
	minWidth: '100px',
	maxWidth: '100px',
	'&:hover': {
		backgroundColor: '#333333',
		color: '#FFFFFF',
		fontWeight: 'bold',
	},
}));
